import type {
    PricingConfigPayload,
    PricingConfigSummary,
    PricingConfigUnitType,
} from '../../../services/api';
import { isPricingConfigPayload } from '../adminDashboardHelpers';

const MAX_PRICING_SAMPLE_SIZES = 10;

export interface PricingSampleSizeFormRow {
    key: string;
    n: string;
    multiplier: string;
}

export interface PricingConfigFormValues {
    name: string;
    description: string;
    unitType: PricingConfigUnitType;
    supportsPricingEstimates: boolean;
    sentimentMapMultiplier: string;
    b2bCredits: string;
    b2cCredits: string;
    sampleSizes: PricingSampleSizeFormRow[];
    defaultSampleSize: string;
    standardPulse: string;
}

const DEFAULT_PRICING_FORM_VALUES: PricingConfigFormValues = {
    name: '',
    description: '',
    unitType: 'credit',
    supportsPricingEstimates: true,
    sentimentMapMultiplier: '1',
    b2bCredits: '2',
    b2cCredits: '1',
    sampleSizes: [{ key: 'default-120', n: '120', multiplier: '1' }],
    defaultSampleSize: '120',
    standardPulse: '1',
};

function newRowKey(): string {
    return crypto.randomUUID();
}

/** Aligns with server `readStoredPricingUnitType`: legacy configs without `unitType` are credit. */
export function readPricingConfigUnitType(summary: PricingConfigSummary): PricingConfigUnitType {
    const raw = (summary.config as { unitType?: unknown }).unitType;
    if (raw === 'token' || raw === 'credit') return raw;
    return 'credit';
}

export function createDefaultPricingConfigFormValues(
    initialUnitType: PricingConfigUnitType = 'credit',
): PricingConfigFormValues {
    return {
        ...DEFAULT_PRICING_FORM_VALUES,
        unitType: initialUnitType,
        sampleSizes: DEFAULT_PRICING_FORM_VALUES.sampleSizes.map((row) => ({
            ...row,
            key: newRowKey(),
        })),
    };
}

export function createEmptyPricingSampleSizeRow(): PricingSampleSizeFormRow {
    return { key: newRowKey(), n: '', multiplier: '' };
}

export function pricingSummaryToFormValues(config: PricingConfigSummary): PricingConfigFormValues {
    if (!isPricingConfigPayload(config.config)) {
        return {
            ...createDefaultPricingConfigFormValues(readPricingConfigUnitType(config)),
            name: config.name,
            description: config.description ?? '',
        };
    }

    return {
        name: config.name,
        description: config.description ?? '',
        unitType: config.config.unitType,
        supportsPricingEstimates: config.config.pro.supportsPricingEstimates,
        sentimentMapMultiplier: String(config.config.pro.sentimentMapMultiplier),
        b2bCredits: String(config.config.pro.b2bCredits),
        b2cCredits: String(config.config.pro.b2cCredits),
        sampleSizes: [...config.config.pro.sampleSizes]
            .sort((a, b) => a.n - b.n)
            .map((sampleSize) => ({
                key: newRowKey(),
                n: String(sampleSize.n),
                multiplier: String(sampleSize.multiplier),
            })),
        defaultSampleSize: String(config.config.pro.defaultSampleSize),
        standardPulse: String(config.config.pulse.standardPulse),
    };
}

export function parsePricingConfigPayload(values: PricingConfigFormValues): PricingConfigPayload {
    if (!values.name.trim()) throw new Error('Name is required.');

    const sentimentMapMultiplier = Number(values.sentimentMapMultiplier);
    const b2bCredits = Number(values.b2bCredits);
    const b2cCredits = Number(values.b2cCredits);
    const standardPulse = Number(values.standardPulse);
    const defaultSampleSize = Number(values.defaultSampleSize);
    if (!Number.isFinite(sentimentMapMultiplier) || sentimentMapMultiplier < 0) {
        throw new Error('Sentiment map multiplier must be a non-negative number.');
    }
    if (!Number.isFinite(b2bCredits) || b2bCredits < 0) {
        throw new Error('B2B credits must be a non-negative number.');
    }
    if (!Number.isFinite(b2cCredits) || b2cCredits < 0) {
        throw new Error('B2C credits must be a non-negative number.');
    }
    if (!Number.isFinite(standardPulse) || standardPulse < 0) {
        throw new Error('Standard Pulse must be a non-negative number.');
    }
    if (!Number.isInteger(defaultSampleSize) || defaultSampleSize <= 0) {
        throw new Error('Default sample size must be a positive whole number.');
    }
    if (values.sampleSizes.length < 1 || values.sampleSizes.length > MAX_PRICING_SAMPLE_SIZES) {
        throw new Error(`Add between 1 and ${MAX_PRICING_SAMPLE_SIZES} sample sizes.`);
    }

    const seenSampleSizes = new Set<number>();
    const sampleSizes = values.sampleSizes
        .map((row) => {
            const n = Number(row.n);
            const multiplier = Number(row.multiplier);
            if (!Number.isInteger(n) || n <= 0) {
                throw new Error('Each sample size must be a positive whole number.');
            }
            if (!Number.isFinite(multiplier) || multiplier <= 0) {
                throw new Error('Each sample size multiplier must be a positive number.');
            }
            if (seenSampleSizes.has(n)) {
                throw new Error('Sample size values must be unique.');
            }
            seenSampleSizes.add(n);
            return { n, multiplier };
        })
        .sort((a, b) => a.n - b.n);

    if (!seenSampleSizes.has(defaultSampleSize)) {
        throw new Error('Default sample size must match one of the sample size rows.');
    }

    return {
        unitType: values.unitType,
        pro: {
            supportsPricingEstimates: values.supportsPricingEstimates,
            sentimentMapMultiplier,
            b2bCredits,
            b2cCredits,
            sampleSizes,
            defaultSampleSize,
        },
        pulse: {
            standardPulse,
        },
    };
}
